import React from 'react';
import './Footer.scss';

interface FooterLink {
  label: string;
  href: string;
  target?: string;
  color?: string;
  ['link-color']?: string;
  ['link-decoration']?: string;
  ['font-size']?: string;
  ['aria-label']?: string;
  ['data-label']?: string;
}

interface FooterConfig {
  disclaimer?: string;
  copyright?: string;
  backcolor?: string;
  links?: FooterLink[];
  bodyStyle?: { color?: string };
}

export function Footer({ footer }: { footer?: FooterConfig }) {
  const [appliedBackground, setAppliedBackground] = React.useState<string>('#9e9e9e7d');
  const [footerSettings, setFooterSettings] = React.useState<FooterConfig | null>(null);

  React.useEffect(() => {
    const applyFooter = (cfg: any) => {
      if (!cfg) return false;
      const footerCfg = cfg?.footer || cfg?.settings?.footer || cfg;
      const hasFooter =
        footerCfg &&
        (footerCfg.backcolor ||
          footerCfg.disclaimer ||
          footerCfg.copyright ||
          (Array.isArray(footerCfg.links) && footerCfg.links.length > 0));
      if (!hasFooter) return false;
      setFooterSettings(footerCfg);
      setAppliedBackground(footerCfg?.backcolor || '#551540');
      console.log('[Footer] applied footer config', footerCfg);
      return true;
    };

    try {
      const sb = localStorage.getItem('SelectedBot');
      if (sb) {
        const parsed = JSON.parse(sb);
        console.debug('[Footer] using SelectedBot.footer', parsed?.footer);
        if (applyFooter(parsed)) return; // stop here; no cookies / no caches
      }
    } catch (e) {
      console.warn('[Footer] SelectedBot parse failed', e);
    }

    try {
      const bs = localStorage.getItem('botSettings');
      if (bs) {
        const parsed = JSON.parse(bs);
        console.debug('[Footer] using botSettings.footer', parsed?.footer);
        if (applyFooter(parsed)) return;
      }
    } catch (err) {
      console.warn('[Footer] botSettings parse failed', err);
    }

  

    try {
      const orgCookie = document.cookie
        .split('; ')
        .find((row) => row.startsWith('org='))
        ?.split('=')[1];

      if (orgCookie) {
        const parsed = JSON.parse(decodeURIComponent(orgCookie));
        console.debug('[Footer] using org cookie', parsed?.footer);
      }
    } catch (err) {
      console.error('Failed to parse org cookie:', err);
    }

    console.debug('[Footer] No footer config found; using defaults');
  }, []);

  // React to live language-mode updates dispatched by IndexPage after translation
  React.useEffect(() => {
    const onUpdated = (e: Event) => {
      const detail = (e as CustomEvent<any>).detail;
      const footerCfg = detail?.footer;
      if (!footerCfg) return;
      setFooterSettings(footerCfg);
      if (footerCfg.backcolor) setAppliedBackground(footerCfg.backcolor);
    };

    const onReverted = (e: Event) => {
      const detail = (e as CustomEvent<any>).detail;
      const footerCfg = detail?.footer;
      if (!footerCfg) return;
      setFooterSettings(footerCfg);
      if (footerCfg.backcolor) setAppliedBackground(footerCfg.backcolor);
    };

    window.addEventListener('botSettingsUpdated', onUpdated);
    window.addEventListener('botSettingsReverted', onReverted);
    return () => {
      window.removeEventListener('botSettingsUpdated', onUpdated);
      window.removeEventListener('botSettingsReverted', onReverted);
    };
  }, []);

  const activeFooter = React.useMemo(() => footerSettings || footer || {}, [footerSettings, footer]);
  const linkDefaultColor =
    activeFooter?.links?.[0]?.['link-color'] ||
    activeFooter?.links?.[0]?.color ||
    activeFooter?.bodyStyle?.color;

  React.useEffect(() => {
    console.log('[Footer] render activeFooter', activeFooter);
  }, [activeFooter]);


  return (
    <footer
      data-name="footer-root"
      style={{
        background: activeFooter?.backcolor || appliedBackground,
        color: linkDefaultColor,
        textAlign: 'center',
        fontSize: '0.8rem',
        padding: '1vh 0',
        ...(activeFooter as any)?.style,
      }}
    >
      <span
        id="disclaimer-text"
        style={{
          backgroundColor: 'transparent',
          display: 'block'
        }}
      >
        {activeFooter?.disclaimer || 'BETA'}
      </span>

      <div className="footer-links" style={{ display: 'block', marginTop: 8 }}>
        <span>
          {activeFooter?.copyright || 'AI Generated Response'}
        </span>
        <br />
        {(activeFooter?.links)?.map(
          (link: FooterLink, index: number) => (
            <React.Fragment key={index}>
              {index > 0 && ' | ' /* the same &nbsp;|&nbsp; the sign-in pages put between links */}
              <a
                target={link.target || '_blank'}
                href={link.href}
                data-name={`footer-link-${link.label || `item-${index}`}`}
                aria-label={link['aria-label'] || link.label}
                data-label={link['data-label'] || link.label}
                style={{
                  color: link['link-color'] || link.color || linkDefaultColor,
                  fontSize: link['font-size'] || '0.8rem',
                  textDecoration: link['link-decoration'] || 'none',
                }}
              >
                <span>{link.label}</span>
              </a>
            </React.Fragment>
          )
        )}
      </div>
    </footer>
  );
}
